import { Target, Globe, Users, BarChart3, Monitor, Zap } from 'lucide-react';

export interface CompetenceData {
  title: string;
  description: string;
  icon: any;
  situation: {
    context: string;
    challenge: string;
    solution: string;
  };
  processus: {
    etape: string;
    description: string;
    duree: string;
  }[];
  resultats: string[];
  outils: string[];
}

export const competencesData: Record<string, CompetenceData> = {
  'strategie-communication': {
    title: 'Stratégie de Communication Globale',
    description: 'Conception et déploiement de plans de communication intégrés',
    icon: Target,
    situation: {
      context: 'Une PME souhaite refondre entièrement sa communication pour gagner en visibilité',
      challenge: 'Communication incohérente, message flou, présence digitale faible',
      solution: 'Audit complet, repositionnement stratégique et plan de communication 360°'
    },
    processus: [
      {
        etape: 'Audit & Diagnostic',
        description: 'Analyse approfondie de l\'existant, concurrence et positionnement actuel',
        duree: '1-2 semaines'
      },
      {
        etape: 'Définition Stratégique',
        description: 'Elaboration du positionnement, messages clés et persona cibles',
        duree: '2-3 semaines'
      },
      {
        etape: 'Plan d\'Action',
        description: 'Création du plan média, budget et timeline détaillés',
        duree: '1 semaine'
      },
      {
        etape: 'Déploiement',
        description: 'Mise en œuvre progressive avec points de contrôle réguliers',
        duree: '3-6 mois'
      }
    ],
    resultats: [
      '+150% de visibilité en ligne',
      'Message unifié sur tous les canaux',
      'Amélioration de l\'image de marque',
      'ROI marketing multiplié par 3'
    ],
    outils: ['HubSpot', 'Google Analytics', 'Canva Pro', 'Monday.com']
  },
  'communication-digitale': {
    title: 'Communication Digitale & Médias Sociaux',
    description: 'Maîtrise complète de l\'écosystème digital',
    icon: Globe,
    situation: {
      context: 'Une startup B2B veut développer sa présence sur LinkedIn pour générer des leads qualifiés',
      challenge: 'Faible engagement, contenu peu adapté, audience non qualifiée',
      solution: 'Stratégie LinkedIn personnalisée avec content marketing ciblé'
    },
    processus: [
      {
        etape: 'Analyse de l\'audience',
        description: 'Identification des personas et comportements sur LinkedIn',
        duree: '1 semaine'
      },
      {
        etape: 'Stratégie de contenu',
        description: 'Calendrier éditorial adapté aux objectifs business',
        duree: '2 semaines'
      },
      {
        etape: 'Optimisation profils',
        description: 'Amélioration des profils entreprise et dirigeants',
        duree: '1 semaine'
      },
      {
        etape: 'Déploiement & suivi',
        description: 'Publication régulière et optimisation continue',
        duree: 'Ongoing'
      }
    ],
    resultats: [
      '+300% d\'engagement LinkedIn',
      '50 nouveaux leads qualifiés/mois',
      'Positionnement d\'expert renforcé',
      'Réseau professionnel élargi'
    ],
    outils: ['LinkedIn Ads', 'Hootsuite', 'Canva Pro', 'Google Analytics']
  },
  'relations-publiques': {
    title: 'Relations Publiques & Médias',
    description: 'Développement de relations durables avec médias et influenceurs',
    icon: Users,
    situation: {
      context: 'Une entreprise technologique veut lancer un nouveau produit et gagner en crédibilité médiatique',
      challenge: 'Aucune relation presse établie, produit complexe à expliquer, marché concurrentiel',
      solution: 'Stratégie RP complète avec storytelling adapté et relations médias ciblées'
    },
    processus: [
      {
        etape: 'Mapping média',
        description: 'Identification des journalistes et influenceurs pertinents',
        duree: '1 semaine'
      },
      {
        etape: 'Création du narrative',
        description: 'Développement de l\'histoire et des messages clés',
        duree: '2 semaines'
      },
      {
        etape: 'Relations presse',
        description: 'Prises de contact et développement des relations',
        duree: '3-4 semaines'
      },
      {
        etape: 'Campagne de lancement',
        description: 'Coordination événement presse et suivi médiatique',
        duree: '2 semaines'
      }
    ],
    resultats: [
      '15 articles dans la presse spécialisée',
      '3 interviews radio/podcast',
      'Couverture médiatique valorisée à 50k€',
      'Notoriété marque +200%'
    ],
    outils: ['Pressbook', 'Mention', 'Cision', 'Google Alerts']
  },
  'analyse-optimisation': {
    title: 'Analyse & Optimisation',
    description: 'Mesure de performance et optimisation continue',
    icon: BarChart3,
    situation: {
      context: 'Un e-commerce souhaite améliorer ses performances marketing et optimiser son ROI publicitaire',
      challenge: 'Données dispersées, KPI non définis, budget publicitaire mal optimisé',
      solution: 'Mise en place d\'un dashboard unifié et stratégie d\'optimisation data-driven'
    },
    processus: [
      {
        etape: 'Audit analytique',
        description: 'Analyse des sources de données et configuration du tracking',
        duree: '1 semaine'
      },
      {
        etape: 'Dashboard custom',
        description: 'Création de tableaux de bord personnalisés',
        duree: '2 semaines'
      },
      {
        etape: 'Tests A/B',
        description: 'Mise en place de tests pour optimiser les conversions',
        duree: '4-6 semaines'
      },
      {
        etape: 'Optimisation continue',
        description: 'Ajustements réguliers basés sur les données',
        duree: 'Ongoing'
      }
    ],
    resultats: [
      'ROI publicitaire +180%',
      'Taux de conversion +45%',
      'Coût d\'acquisition réduit de 30%',
      'Visibilité complète sur les performances'
    ],
    outils: ['Google Analytics', 'Google Tag Manager', 'Hotjar', 'Facebook Pixel']
  },
  'solutions-digitales': {
    title: 'Solutions Digitales',
    description: 'Conception et développement de plateformes innovantes',
    icon: Monitor,
    situation: {
      context: 'Une association veut moderniser sa présence en ligne avec un nouveau site web et automatiser ses processus',
      challenge: 'Site web obsolète, processus manuels chronophages, expérience utilisateur défaillante',
      solution: 'Refonte complète du site avec automatisation des processus et UX optimisée'
    },
    processus: [
      {
        etape: 'Audit UX/UI',
        description: 'Analyse de l\'expérience utilisateur et benchmarks concurrentiels',
        duree: '1 semaine'
      },
      {
        etape: 'Conception & Design',
        description: 'Wireframes, maquettes et prototypes interactifs',
        duree: '3 semaines'
      },
      {
        etape: 'Développement',
        description: 'Intégration technique et mise en place des automatisations',
        duree: '6-8 semaines'
      },
      {
        etape: 'Tests & Déploiement',
        description: 'Tests utilisateurs, corrections et mise en ligne',
        duree: '2 semaines'
      }
    ],
    resultats: [
      'Temps de chargement divisé par 3',
      'Taux de conversion +120%',
      'Processus automatisés à 80%',
      'Satisfaction utilisateur +95%'
    ],
    outils: ['WordPress', 'Figma', 'Google PageSpeed', 'Zapier']
  },
  'innovation-transformation': {
    title: 'Innovation & Transformation',
    description: 'Accompagnement dans l\'adoption de nouvelles technologies',
    icon: Zap,
    situation: {
      context: 'Une PME familiale veut se digitaliser pour rester compétitive face aux nouveaux entrants du marché',
      challenge: 'Résistance au changement, manque de compétences digitales, processus traditionnels ancrés',
      solution: 'Accompagnement change management avec formation équipes et transformation progressive'
    },
    processus: [
      {
        etape: 'Diagnostic transformation',
        description: 'Évaluation de la maturité digitale et identification des leviers',
        duree: '2 semaines'
      },
      {
        etape: 'Roadmap transformation',
        description: 'Plan de transformation progressif avec quick wins',
        duree: '1 semaine'
      },
      {
        etape: 'Formation équipes',
        description: 'Sessions de formation sur les nouveaux outils et processus',
        duree: '4-6 semaines'
      },
      {
        etape: 'Accompagnement',
        description: 'Suivi personnalisé et ajustements pendant la transition',
        duree: '3-6 mois'
      }
    ],
    resultats: [
      'Productivité équipes +60%',
      'Processus digitalisés à 75%',
      'Satisfaction employés +40%',
      'Avantage concurrentiel renforcé'
    ],
    outils: ['Slack', 'Notion', 'Asana', 'Google Workspace']
  }
};
